import { MenuVerticalFilled } from "@aircall/icons";
import {
  Button,
  Dropdown,
  Flex,
  Menu,
  MenuItem,
  Typography,
} from "@aircall/tractor";
import { useTranslation } from "react-i18next";

import { EvaluationComment } from "../EvaluationComment";
import { EvaluationPercentage } from "../EvaluationPercentage";

import { Locale, formatDistanceToNow } from "date-fns";
import { de, enUS, es, fr, it, nb } from "date-fns/locale";
import { EvaluationHeadingProps } from ".";

const localeMap: Record<string, Locale> = {
  en: enUS,
  fr: fr,
  de: de,
  es: es,
  it: it,
  nb: nb,
};

export function EvaluationHeading({
  title,
  disabled,
  totalPoints,
  totalMaximumPoints,
  comment,
  description,
  lastReviewAt,
  lastReviewName,
  isReadonly,
  onClickEdit,
  onClickDelete,
}: EvaluationHeadingProps) {
  const { t, i18n } = useTranslation();
  const locale = localeMap[i18n.language];

  return (
    <Flex
      flexDirection="column"
      gap="xxs"
      p="s"
      backgroundColor="surface-default"
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Flex flexDirection="column" gap="xxxs">
          <Typography variant="headingBoldS">{title}</Typography>
          {Boolean(description) && (
            <Typography variant="bodyRegularM">{description}</Typography>
          )}
        </Flex>
        <Flex gap="xxs" alignItems="center">
          <EvaluationPercentage
            points={totalPoints}
            maximumPoints={totalMaximumPoints}
            disabled={disabled}
          />
          {!isReadonly ? (
            <Dropdown
              data-test="action-dropdown"
              trigger={
                <Button
                  size="xSmall"
                  mode="ghost"
                  variant="secondary"
                  data-test="action-evaluation-button"
                >
                  <MenuVerticalFilled />
                </Button>
              }
            >
              <Menu>
                <MenuItem
                  onClick={onClickEdit}
                  data-test="edit-evaluation-button"
                >
                  {t("evaluation.edit_evaluation")}
                </MenuItem>
                <MenuItem
                  onClick={onClickDelete}
                  data-test="delete-evaluation-button"
                >
                  {t("evaluation.remove")}
                </MenuItem>
              </Menu>
            </Dropdown>
          ) : null}
        </Flex>
      </Flex>
      {Boolean(comment) && <EvaluationComment>{comment}</EvaluationComment>}
      {lastReviewAt && lastReviewName && (
        <Typography variant="supportingRegularS">
          {t("evaluation.last_reviewed", {
            distanceToNow: formatDistanceToNow(new Date(lastReviewAt), {
              addSuffix: true,
              locale,
            }),
            name: lastReviewName,
          })}
        </Typography>
      )}
    </Flex>
  );
}
